import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BlackBar } from '../blackBar/BlackBar';
import { SiteHeader } from '../siteHeader/SiteHeader';
import { getConfirmationMessage, getConfirmationVisible } from 'selectors/shellSelectors';
import { confirmYes, confirmNo, escape } from 'actions/shellActions';
import { ConfirmationDialog } from 'ui/shell/confirmationDialog/ConfirmationDialog';
import { getCurrentUserId } from 'selectors/sessionSelectors';
import { AppRouter } from '../AppRouter';
import { WelcomeArea } from 'ui/welcome/welcomeArea/WelcomeArea';
import { MenuProvider } from 'hooks/useMenu/useMenu';
import { ErrorBoundary } from '@sentry/react';
import { ReloadPage } from '../reloadPage/ReloadPage';

require('semantic-ui-reset/reset.css');
require('semantic-ui-site/site.css');
require('semantic-ui-grid/grid.css');
require('semantic-ui-form/form.css');
require('semantic-ui-list/list.css');
require('semantic-ui-card/card.css');
require('./semantic-ui-icon.css'); // The real semantic-ui-icon never updated its icon files. So we override with our own CSS and fonts
require('semantic-ui-input/input.css');
require('semantic-ui-label/label.css');
require('semantic-ui-loader/loader.css');
require('semantic-ui-message/message.css');
require('semantic-ui-checkbox/checkbox.css');
require('semantic-ui-transition/transition.css');
require('semantic-ui-dropdown/dropdown.css');
require('semantic-ui-button/button.css');
require('semantic-ui-header/header.css');
require('semantic-ui-segment/segment.css');
require('semantic-ui-table/table.css');
require('semantic-ui-divider/divider.css');
require('semantic-ui-popup/popup.css');
require('./App.css');
require('./theme.css');


export const App = () => {
    const confirmationMessage = useSelector(getConfirmationMessage);
    const confirmationVisible = useSelector(getConfirmationVisible);
    const isLoggedIn = !!useSelector(getCurrentUserId);

    const dispatch = useDispatch();

    const onClickConfirmYes = useCallback(() => dispatch(confirmYes()), [ dispatch ]);
    const onClickConfirmNo = useCallback(() => dispatch(confirmNo()), [ dispatch ]);
    const onPressEscapeKey = useCallback(() => dispatch(escape()), [ dispatch ]);

    const handleKeyDown = useCallback(({ keyCode, key }: KeyboardEvent) => {
        if (keyCode === 27 || key === 'Escape') {
            onPressEscapeKey();
        }
    }, [ onPressEscapeKey ]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [ handleKeyDown ]);

    // TODO: perhaps parse the URL to determine if it's visible. For now, just shut the sliding bars off
    const isEditorVisible = false;

    if (!isLoggedIn) {
        return (
            <div className={ `app-container ${isEditorVisible ? 'hide-nav' : ''}` }>
                <WelcomeArea/>
            </div>
        );
    }
    return (
        <div className={ `app-container ${isEditorVisible ? 'hide-nav' : ''}` }>
            <MenuProvider>
                <BlackBar/>
                <SiteHeader/>
                <div id="site-content">
                    <ErrorBoundary fallback={ <ReloadPage/> }>
                        <AppRouter/>
                    </ErrorBoundary>
                </div>
                <ConfirmationDialog
                    visible={ confirmationVisible }
                    message={ confirmationMessage }
                    onClickYes={ onClickConfirmYes }
                    onClickNo={ onClickConfirmNo }
                />
            </MenuProvider>
        </div>
    );
};
